import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';

const WorkflowOverview = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('companyToken') || 
                    localStorage.getItem('authToken') ||
                    localStorage.getItem('branchToken') ||
                    localStorage.getItem('employeeToken');
      
      const response = await axios.get('/leads', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle different response formats
      let allLeads = [];
      if (Array.isArray(response.data)) {
        allLeads = response.data;
      } else if (response.data.leads && Array.isArray(response.data.leads)) {
        allLeads = response.data.leads;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        allLeads = response.data.data;
      }
      
      setLeads(allLeads);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeads([]); // Set empty array on error
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'sales_new': 'bg-blue-100 text-blue-800',
      'sales_working': 'bg-blue-200 text-blue-900',
      'forwarded_to_reservation': 'bg-purple-100 text-purple-800',
      'reservation_working': 'bg-purple-200 text-purple-900',
      'vendor_approval_pending': 'bg-yellow-100 text-yellow-800',
      'vendor_approved': 'bg-green-100 text-green-800',
      'quotation_prepared': 'bg-indigo-100 text-indigo-800',
      'quotation_sent': 'bg-cyan-100 text-cyan-800',
      'client_approved': 'bg-green-200 text-green-900',
      'forwarded_to_accounts': 'bg-orange-100 text-orange-800',
      'payment_pending': 'bg-yellow-200 text-yellow-900',
      'completed': 'bg-green-300 text-green-900',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getOwnerColor = (owner) => {
    const colors = {
      'sales': 'bg-blue-100 text-blue-800',
      'reservation': 'bg-purple-100 text-purple-800',
      'accounts': 'bg-orange-100 text-orange-800',
      'client': 'bg-gray-100 text-gray-800'
    };
    return colors[owner] || 'bg-gray-100 text-gray-800';
  };

  const filteredLeads = filterStatus === 'all' 
    ? (Array.isArray(leads) ? leads : [])
    : (Array.isArray(leads) ? leads.filter(lead => lead.currentOwner === filterStatus) : []);

  const stats = {
    total: Array.isArray(leads) ? leads.length : 0,
    sales: Array.isArray(leads) ? leads.filter(l => l.currentOwner === 'sales').length : 0,
    reservation: Array.isArray(leads) ? leads.filter(l => l.currentOwner === 'reservation').length : 0,
    accounts: Array.isArray(leads) ? leads.filter(l => l.currentOwner === 'accounts').length : 0,
    completed: Array.isArray(leads) ? leads.filter(l => l.workflowStatus === 'completed').length : 0,
    revenue: Array.isArray(leads) ? leads.reduce((sum, l) => sum + (l.quotations?.[0]?.amount || 0), 0) : 0
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Workflow Overview</h2>
        <p className="text-gray-600 mt-1">Complete view of all leads across all teams</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">Total Leads</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">With Sales</p>
          <p className="text-3xl font-bold text-blue-600 mt-1">{stats.sales}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">With Reservation</p>
          <p className="text-3xl font-bold text-purple-600 mt-1">{stats.reservation}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">With Accounts</p>
          <p className="text-3xl font-bold text-orange-600 mt-1">{stats.accounts}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">Completed</p>
          <p className="text-3xl font-bold text-green-600 mt-1">{stats.completed}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-gray-500 text-sm">Total Revenue</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">₹{stats.revenue.toLocaleString()}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg ${filterStatus === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            All ({stats.total})
          </button>
          <button
            onClick={() => setFilterStatus('sales')}
            className={`px-4 py-2 rounded-lg ${filterStatus === 'sales' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            Sales ({stats.sales})
          </button>
          <button
            onClick={() => setFilterStatus('reservation')}
            className={`px-4 py-2 rounded-lg ${filterStatus === 'reservation' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            Reservation ({stats.reservation})
          </button>
          <button
            onClick={() => setFilterStatus('accounts')}
            className={`px-4 py-2 rounded-lg ${filterStatus === 'accounts' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            Accounts ({stats.accounts})
          </button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Team</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quotations</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredLeads.map((lead) => (
              <tr key={lead._id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <div className="font-medium text-gray-900">{lead.customerName}</div>
                    <div className="text-sm text-gray-500">{lead.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{lead.destination || 'N/A'}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(lead.workflowStatus)}`}>
                    {lead.workflowStatus?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${getOwnerColor(lead.currentOwner)}`}>
                    {lead.currentOwner}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <div className="font-medium">Sent: {lead.quotations?.filter(q => q.status === 'sent').length || 0}</div>
                    <div className="text-gray-500">Total: {lead.quotations?.length || 0}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="font-semibold text-gray-900">
                    {lead.quotations?.[0]?.amount ? `₹${lead.quotations[0].amount.toLocaleString()}` : 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => setSelectedLead(lead)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Lead Details Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Lead Details</h2>
              <button
                onClick={() => setSelectedLead(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Customer Information</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Name:</strong> {selectedLead.customerName}</p>
                  <p><strong>Email:</strong> {selectedLead.email}</p>
                  <p><strong>Phone:</strong> {selectedLead.contactNumber}</p>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Travel Details</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Destination:</strong> {selectedLead.destination || 'N/A'}</p>
                  <p><strong>Days:</strong> {selectedLead.numberOfDays || 'N/A'}</p>
                  <p><strong>Budget:</strong> {selectedLead.budget || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Workflow History */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Workflow History</h3>
              <div className="space-y-3">
                {selectedLead.workflowHistory?.length > 0 ? (
                  selectedLead.workflowHistory.map((history, idx) => (
                    <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2 bg-gray-50 rounded">
                      <p className="font-medium">{history.action}</p>
                      <p className="text-sm text-gray-600">
                        {history.fromStatus} → {history.toStatus}
                      </p>
                      <p className="text-xs text-gray-500">
                        By {history.performedByName} on {new Date(history.timestamp).toLocaleString()}
                      </p>
                      {history.notes && <p className="text-sm text-gray-600 mt-1">{history.notes}</p>}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No workflow history yet</p>
                )}
              </div>
            </div>

            {/* Quotations */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3">Quotations ({selectedLead.quotations?.length || 0})</h3>
              <div className="space-y-3">
                {selectedLead.quotations?.length > 0 ? (
                  selectedLead.quotations.map((quot, idx) => (
                    <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">Quotation #{quot.quotationNumber}</p>
                          <p className="text-sm text-gray-600">Amount: ₹{quot.amount?.toLocaleString()}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Prepared by {quot.preparedByName}
                          </p>
                          {quot.sentByName && (
                            <p className="text-xs text-gray-500">
                              Sent by {quot.sentByName} on {new Date(quot.sentDate).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          quot.status === 'approved' ? 'bg-green-100 text-green-800' :
                          quot.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {quot.status}
                        </span>
                      </div>
                      {quot.notes && <p className="text-sm text-gray-600 mt-2">{quot.notes}</p>}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No quotations yet</p>
                )}
              </div>
            </div>

            {/* Vendor Approvals */}
            {selectedLead.vendorApprovals?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Vendor Approvals</h3>
                <div className="space-y-3">
                  {selectedLead.vendorApprovals.map((approval, idx) => (
                    <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{approval.vendorName}</p>
                          <p className="text-xs text-gray-500">
                            Requested by {approval.requestedByName} on {new Date(approval.requestDate).toLocaleDateString()}
                          </p>
                          {approval.notes && <p className="text-sm text-gray-600 mt-1">{approval.notes}</p>}
                        </div>
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          approval.approvalStatus === 'approved' ? 'bg-green-100 text-green-800' :
                          approval.approvalStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {approval.approvalStatus}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowOverview;
